/// PatPreDagWorkspace.jsx
// LLM distillation review + human semantic stabilisation
// This panel MUST ONLY render from pat_distillation output

import React, { useState, useEffect } from "react";

function buildInitialRows(dist) {
  const part = dist?.suggested_partition || {};
  const candidates = dist?.candidate_elements || [];

  const rows = [];

  (part.X || []).forEach((label, i) =>
    rows.push({
      _id: `X-${i}`,
      label,
      role: "X",
      sourceSentence: "",
    }),
  );

  (part.M || []).forEach((label, i) =>
    rows.push({
      _id: `M-${i}`,
      label,
      role: "M",
      sourceSentence: "",
    }),
  );

  (part.Z || []).forEach((label, i) =>
    rows.push({
      _id: `Z-${i}`,
      label,
      role: "Z",
      sourceSentence: "",
    }),
  );

  if (part.Y) {
    rows.push({
      _id: "Y-0",
      label: part.Y,
      role: "Y",
      sourceSentence: "",
    });
  }
  // --------------------------------------------------
  // Seed rows from candidate_elements if partition empty
  // --------------------------------------------------
  if (
    rows.length <= 1 && // only Y or empty
    candidates.length > 0
  ) {
    candidates.forEach((label, i) => {
      if (label !== part.Y) {
        rows.push({
          _id: `C-${i}`,
          label,
          role: "X",
          sourceSentence: "",
        });
      }
    });
  }

  return rows;
}

function RoleSelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200"
    >
      <option value="X">X</option>
      <option value="M">M</option>
      <option value="Z">Z</option>
      <option value="Y">Y</option>
    </select>
  );
}

function RowEditor({ row, onLabel, onRole, onDelete }) {
  return (
    <div className="flex items-center gap-2">
      <RoleSelect value={row.role} onChange={onRole} />
      <input
        value={row.label}
        onChange={(e) => onLabel(e.target.value)}
        className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200"
      />
      <button
        type="button"
        onClick={onDelete}
        className="text-xs text-slate-400 hover:text-slate-200"
        title="Remove"
      >
        ✕
      </button>
    </div>
  );
}

function Section({ title, rows, onUpdateRow, onDeleteRow, onAdd }) {
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 p-5 space-y-4">
      <div className="relative group inline-block cursor-help text-sm font-semibold text-slate-200">
        {typeof title === "object" ? title.label : title}

        {typeof title === "object" && (
          <div
            className="absolute left-0 top-full mt-1 hidden group-hover:block
                            bg-black text-xs text-slate-200 px-2 py-1 rounded
                            border border-slate-700 whitespace-nowrap z-50"
          >
            {title.tooltip}
          </div>
        )}
      </div>

      {rows.length ? (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row._id} className="space-y-1">
              <RowEditor
                row={row}
                onLabel={(val) => onUpdateRow(row._id, { label: val })}
                onRole={(val) => onUpdateRow(row._id, { role: val })}
                onDelete={() => onDeleteRow(row._id)}
              />
              {row.sourceSentence ? (
                <div className="text-[11px] text-slate-500">
                  {row.sourceSentence}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-400">No candidates.</div>
      )}

      <button
        type="button"
        onClick={onAdd}
        className="text-xs text-sky-400 hover:underline"
      >
        + add
      </button>
    </div>
  );
}

export default function PatPreDagWorkspace({
  patDistillation,
  onAdoptDistillation,
}) {
  // --------------------------------------------------
  // HARD RENDER GUARD (SOP)
  // Render ONLY when backend has explicitly
  // produced a Pre-DAG distillation
  // --------------------------------------------------
  if (
    !patDistillation ||
    patDistillation.status !== "ready_for_pre_dag_workspace"
  ) {
    return null;
  }

  if (!patDistillation.suggested_partition) {
    console.error(
      "PatPreDagWorkspace: distillation missing suggested_partition",
      patDistillation,
    );
    return null;
  }

  const llmDistillation = patDistillation || {};

  const distillationSignature = JSON.stringify({
    status: patDistillation?.status || "",
    suggested_partition: patDistillation?.suggested_partition || {},
    candidate_elements: patDistillation?.candidate_elements || [],
  });

  // --------------------------------------------------
  // Initialise from current distillation payload
  // and resynchronise only when distillation content changes.
  // Do not reset local user edits merely because the parent
  // supplies a fresh object reference.
  // --------------------------------------------------
  const [rows, setRows] = useState(() => buildInitialRows(llmDistillation));
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    setRows(buildInitialRows(llmDistillation));
    setAcknowledged(false);
  }, [distillationSignature]);

  // Enforce single-Y inside Pre-DAG: if you set a new Y, demote previous Y to X.
  const updateRow = (_id, patch) => {
    setRows((prev) => {
      const next = prev.map((r) => (r._id === _id ? { ...r, ...patch } : r));

      if (patch.role === "Y") {
        let seenY = false;
        for (let i = 0; i < next.length; i++) {
          if (next[i].role === "Y") {
            if (!seenY) {
              seenY = true;
            } else {
              next[i] = { ...next[i], role: "X" };
            }
          }
        }
      }
      return next;
    });
  };

  const deleteRow = (_id) =>
    setRows((prev) => prev.filter((r) => r._id !== _id));

  const addRow = (role) => {
    setRows((prev) => [
      ...prev,
      { _id: `${Date.now()}-${role}`, label: "", role, sourceSentence: "" },
    ]);
  };

  const byRole = (role) => rows.filter((r) => r.role === role);

  const canContinue = () => {
    const yCount = byRole("Y").filter((r) => r.label.trim()).length;
    return yCount === 1; // enforce “exactly one Y” early
  };

  const handleContinue = () => {
    if (typeof onAdoptDistillation !== "function") return;

    // emit draft (no IDs, no commitment)
    onAdoptDistillation({
      X: byRole("X")
        .map((r) => r.label)
        .filter(Boolean),
      M: byRole("M")
        .map((r) => r.label)
        .filter(Boolean),
      Z: byRole("Z")
        .map((r) => r.label)
        .filter(Boolean),
      Y: (byRole("Y")[0]?.label || "").trim(),
    });
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 p-5 space-y-6">
      <div className="text-sm font-bold text-slate-200 leading-relaxed">
        PAT-GPT identified candidate concepts in your prompt, however causal
        interpretation cannot proceed until provisional variable roles are
        assigned.
      </div>

      <div className="text-sm text-slate-300 leading-relaxed mt-3">
        Nominate exactly one focal outcome (Y), then review whether the
        remaining concepts function as candidate causes, exposures, conditions,
        or interventions (X), pathway variables (M), or background variables (Z)
        within the proposed causal structure.
      </div>

      {rows.length === 0 && (
        <div className="rounded-lg border border-amber-700/60 bg-amber-950/20 p-3 text-xs text-amber-200 leading-relaxed">
          PAT-GPT did not extract provisional variables from this input. Add one
          focal outcome (Y), then add any relevant X, M, or Z variables
          manually.
        </div>
      )}

      <div className="space-y-5">
        <Section
          title={{
            label: "Focal outcome (Y — exactly one)",
            tooltip:
              "Y — the focal outcome the causal model aims to explain. Exactly one must be nominated.",
          }}
          rows={byRole("Y")}
          onUpdateRow={updateRow}
          onDeleteRow={deleteRow}
          onAdd={() => addRow("Y")}
        />

        <Section
          title={{
            label:
              "Candidate causes, exposures, conditions, or interventions (X)",
            tooltip:
              "X — candidate predictors: variables that may influence the outcome (Y)",
          }}
          rows={byRole("X")}
          onUpdateRow={updateRow}
          onDeleteRow={deleteRow}
          onAdd={() => addRow("X")}
        />

        <Section
          title={{
            label: "Candidate pathway variables (M)",
            tooltip:
              "M — candidate pathway variables that may transmit influence between X and Y",
          }}
          rows={byRole("M")}
          onUpdateRow={updateRow}
          onDeleteRow={deleteRow}
          onAdd={() => addRow("M")}
        />

        <Section
          title={{
            label: "Candidate background variables (Z)",
            tooltip:
              "Z — candidate background variables that may influence candidate X and the outcome (Y)",
          }}
          rows={byRole("Z")}
          onUpdateRow={updateRow}
          onDeleteRow={deleteRow}
          onAdd={() => addRow("Z")}
        />
      </div>

      <div className="border-t border-slate-700 pt-4 space-y-4">
        <label className="flex items-start gap-2 text-xs text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>
            I confirm that these provisional variable roles reasonably reflect
            the causal question I want PAT-GPT to model.
          </span>
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue() || !acknowledged}
            className="px-4 py-2 text-xs rounded bg-indigo-600 text-white disabled:opacity-40"
          >
            Confirm variables and continue
          </button>
        </div>
      </div>

      <p className="text-[15px] text-slate-500 italic">
        This panel stabilises variable meaning. Causal structure is confirmed
        later using the Direct Acyclic Graph (DAG).
      </p>
    </div>
  );
}
